import { TrackingEventName } from '@/models/analytics/eventTypes';
import { trackSelfDescribingEvent } from '@snowplow/browser-tracker';
import {
  trackSessionCopiedSpec,
  trackSessionCreatedSpec,
  trackSessionDeletedSpec,
  trackSessionMarkedAsContextOnlySpec,
  trackSessionSearchUsageBaselineSpec,
  trackSessionsFilterAppliedSpec,
  trackSessionsListPaginatedSpec,
  trackSessionsScopeChangedSpec,
  trackSessionsSortChangedSpec,
  trackSessionUpdatedSpec,
  trackSessionViewedSpec,
  type SessionCopied,
  type SessionCreated,
  type SessionDeleted,
  type SessionMarkedAsContextOnly,
  type SessionSearchUsageBaseline,
  type SessionsFilterApplied,
  type SessionsListPaginated,
  type SessionsScopeChanged,
  type SessionsSortChanged,
  type SessionUpdated,
  type SessionViewed,
} from '@snowtype/snowplow';

import type { SessionLayoutSwitchedEventProps } from '@/models/analytics/features/session-layout';
import type {
  SessionDetailResumeStartedEventProps,
  SessionTabSwitchedEventProps,
  SessionTabViewedEventProps,
} from '@/models/analytics/features/sessions';

import type { SnowplowEventMap } from '../trackSnowplowEvent';

export const sessionEvents: SnowplowEventMap = {
  [TrackingEventName.SessionCreated]: (props) =>
    trackSessionCreatedSpec(props as SessionCreated),
  [TrackingEventName.SessionViewed]: (props) =>
    trackSessionViewedSpec(props as SessionViewed),
  [TrackingEventName.SessionCopied]: (props) =>
    trackSessionCopiedSpec(props as SessionCopied),
  [TrackingEventName.SessionUpdated]: (props) =>
    trackSessionUpdatedSpec(props as SessionUpdated),
  [TrackingEventName.SessionDeleted]: (props) =>
    trackSessionDeletedSpec(props as SessionDeleted),
  [TrackingEventName.SessionSearchUsageBaseline]: (props) =>
    trackSessionSearchUsageBaselineSpec(props as SessionSearchUsageBaseline),
  [TrackingEventName.SessionsFilterApplied]: (props) =>
    trackSessionsFilterAppliedSpec(props as SessionsFilterApplied),
  [TrackingEventName.SessionsScopeChanged]: (props) =>
    trackSessionsScopeChangedSpec(props as SessionsScopeChanged),
  [TrackingEventName.SessionsSortChanged]: (props) =>
    trackSessionsSortChangedSpec(props as SessionsSortChanged),
  [TrackingEventName.SessionsListPaginated]: (props) =>
    trackSessionsListPaginatedSpec(props as SessionsListPaginated),
  [TrackingEventName.SessionMarkedAsContextOnly]: (props) =>
    trackSessionMarkedAsContextOnlySpec(props as SessionMarkedAsContextOnly),
  // Amplitude and PostHog retain the rollout-specific event name, but Snowplow
  // follows the UI taxonomy rather than introducing another bespoke event. This
  // maps the same user action into the already-published ui_interaction schema.
  [TrackingEventName.SessionLayoutSwitched]: (props) => {
    const { direction, source } = props as SessionLayoutSwitchedEventProps;
    return trackSelfDescribingEvent({
      event: {
        schema: 'iglu:com.heidi/ui_interaction/jsonschema/1-0-1',
        data: {
          action_type: 'toggle',
          element_type: 'toggle',
          element_name: 'session_layout',
          element_variant: direction,
          new_value: direction === 'v1_to_v2' ? 'on' : 'off',
          page_section: source,
        },
      },
    });
  },
  [TrackingEventName.SessionTabViewed]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: 'iglu:com.heidi/session_tab_viewed/jsonschema/1-0-0',
        data: props as SessionTabViewedEventProps,
      },
    }),
  [TrackingEventName.SessionTabSwitched]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: 'iglu:com.heidi/session_tab_switched/jsonschema/1-0-0',
        data: props as SessionTabSwitchedEventProps,
      },
    }),
  /* Hand-rolled like the chronicle.ts hardware entries — the checked-in
     Snowtype output can't be regenerated here (console API credentials live
     in 1Password / CI). TODO(snowtype): swap to the generated Spec function
     once snowtype/snowplow.ts is regenerated. The URI must match
     data-structures/com.heidi/session_detail_resume_started.yaml exactly. */
  [TrackingEventName.SessionDetailResumeStarted]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: 'iglu:com.heidi/session_detail_resume_started/jsonschema/1-0-0',
        data: props as SessionDetailResumeStartedEventProps,
      },
    }),
};
